// src/pages/FaleConosco/FAQ.tsx
import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';

const FAQ: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontFamily: 'Playfair Display, serif',
            fontWeight: 600,
            color: 'primary.main',
            textAlign: 'center',
            mb: 4,
          }}
        >
          Dúvidas Frequentes
        </Typography>

        {/* PERGUNTA 1 */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" mb={1}>
            <HelpOutlineIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={600}>
              Como me inscrever em um curso?
            </Typography>
          </Box>

          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            Opção 1 – Pelo site:
          </Typography>
          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemIcon>
                <SchoolIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Curso de Graduação:</strong> Acesse{' '}
                    <em>Cursos → Cursos de Graduação</em>, escolha o curso de
                    interesse e clique em <em>Mais Informações</em> e depois em{' '}
                    <em>Inscrição</em> para a unidade desejada.
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <SchoolIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Curso de Pós-graduação:</strong> Acesse{' '}
                    <em>Cursos → Cursos de Pós-graduação</em>, escolha o curso e
                    clique em <em>Mais Informações</em> →{' '}
                    <em>Registrar Interesse</em>. A equipe entrará em contato
                    com você.
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <SchoolIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Curso de Extensão Universitária:</strong> Acesse{' '}
                    <em>Cursos → Extensão Universitária</em>, escolha o curso e
                    clique em <em>Mais Informações</em> →{' '}
                    <em>Inscrição</em> para a unidade de interesse.
                  </>
                }
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            Opção 2 – Presencialmente nas unidades da Faculdade de Tecnologia Senac Amazonas:
          </Typography>
          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemIcon>
                <AssignmentIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Faculdade Senac:</strong> Rua 10 de Julho Nº 11 – Centro
                    <br />
                    <em>Atendimento:</em> Segunda a Sexta, 09h00 às 17h00
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <AssignmentIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Faculdade Senac Centro:</strong> Rua Saldanha Marinho Nº 410 – Centro
                    <br />
                    <em>Atendimento:</em> Segunda a Sexta, 17h00 às 21h00
                  </>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <AssignmentIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <strong>Centro de Informática:</strong> Rua Darcy Vargas Nº 288 – Chapada
                    <br />
                    <em>Atendimento:</em> Segunda a Sexta, 17h00 às 21h00
                  </>
                }
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* PERGUNTA 2 */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" mb={1}>
            <HelpOutlineIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={600}>
              Quais documentos são necessários para matrícula?
            </Typography>
          </Box>

          <Typography mt={2}>
            Para efetuar sua matrícula, é necessário apresentar os seguintes
            documentos (originais e cópias):
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            📄 Documentos pessoais:
          </Typography>
          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemText primary="RG (Carteira de Identidade) ou, no caso de estrangeiro, RNM ou Passaporte;" />
            </ListItem>
            <ListItem>
              <ListItemText primary="CPF (Cadastro de Pessoa Física);" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Certidão de Nascimento ou Casamento." />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            🏫 Documentos escolares:
          </Typography>
          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemText primary="Histórico Escolar do Ensino Médio;" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Certificado de Conclusão do Ensino Médio." />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            🏠 Comprovante de residência atualizado:
          </Typography>
          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemText primary="Conta de água, luz ou telefone recente." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* PERGUNTA 3 */}
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <HelpOutlineIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={600}>
              Como entrar em contato com a secretaria?
            </Typography>
          </Box>

          <List dense sx={{ pl: 4 }}>
            <ListItem>
              <ListItemIcon>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="secretaria.faculdade@am.senac.br" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PhoneIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="(92) 3199-9956" />
            </ListItem>
          </List>
        </Box>
      </Paper>
    </Container>
  );
};

export default FAQ;
